import type {EventListSummary} from '@/apiTypes/eventList';
import {useAuth} from '@/components/AuthProvider';
import {CreateListModal} from '@/components/createListModal/createListModal';
import {Button, buttonVariants} from '@/components/ui/button';
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import {Modal} from '@/components/ui/modal';
import {toastManager} from '@/components/ui/toast';
import {useIsMobile} from '@/hooks/useIsMobile';
import {addEventToList, removeEventFromList} from '@/server/savedEvent';
import {useNavigate, useRouter} from '@tanstack/react-router';
import {Bookmark, Check, Globe, Lock, Plus} from 'lucide-react';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

const PENDING_SAVE_KEY = 'pending_save_event';

/** Stores a pending save eventId in sessionStorage before login redirect. */
function savePendingSave(eventId: string) {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_SAVE_KEY, eventId);
}

/** Reads and clears a pending save from sessionStorage. */
function consumePendingSave(eventId: string): boolean {
  if (typeof window === 'undefined') return false;
  const stored = sessionStorage.getItem(PENDING_SAVE_KEY);
  if (stored !== eventId) return false;
  sessionStorage.removeItem(PENDING_SAVE_KEY);
  return true;
}

interface AddToListButtonProps {
  eventId: string;
  lists: EventListSummary[];
  memberListIds: string[];
}

/** Shared inner content for a single list row. */
function ListRowContent({
  list,
  isMember,
}: {
  list: EventListSummary;
  isMember: boolean;
}) {
  return (
    <>
      <span className="w-4 shrink-0">
        {isMember && <Check className="size-4 text-primary" />}
      </span>
      <span className="flex-1 truncate">{list.name}</span>
      {list.visibility === 'PUBLIC' ? (
        <Globe className="size-3.5 text-muted-foreground shrink-0" />
      ) : (
        <Lock className="size-3.5 text-muted-foreground shrink-0" />
      )}
    </>
  );
}

interface ListItemsProps {
  lists: EventListSummary[];
  memberIds: Set<string>;
  onToggle: (listId: string, listName: string) => void;
}

/**
 * Renders list rows as plain buttons for use inside the mobile Modal.
 */
function ListItems({lists, memberIds, onToggle}: ListItemsProps) {
  return (
    <>
      {lists.map((list) => (
        <button
          key={list.id}
          type="button"
          className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer text-left"
          onClick={() => onToggle(list.id, list.name)}
        >
          <ListRowContent list={list} isMember={memberIds.has(list.id)} />
        </button>
      ))}
    </>
  );
}

/**
 * Renders list rows as MenuItems for use inside the desktop Menu.
 * Provides proper role="menuitem" and keyboard navigation.
 */
function MenuListItems({lists, memberIds, onToggle}: ListItemsProps) {
  return (
    <>
      {lists.map((list) => (
        <MenuItem key={list.id} onClick={() => onToggle(list.id, list.name)}>
          <ListRowContent list={list} isMember={memberIds.has(list.id)} />
        </MenuItem>
      ))}
    </>
  );
}

/**
 * Button that opens a menu (desktop) or modal (mobile) for toggling
 * event membership across the user's event lists. Replaces the old
 * SaveEventButton with multi-list support.
 *
 * Uses optimistic state updates with rollback on error.
 * Redirects unauthenticated users to login with sessionStorage-based
 * pending action.
 */
export const AddToListButton: FC<AddToListButtonProps> = ({
  eventId,
  lists,
  memberListIds: initialMemberListIds,
}) => {
  const {profile} = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const isMobile = useIsMobile();

  const [memberIds, setMemberIds] = useState(
    () => new Set(initialMemberListIds)
  );
  const memberIdsRef = useRef(memberIds);
  memberIdsRef.current = memberIds;
  const inflight = useRef(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);

  // Sync from server data when not mid-flight
  useEffect(() => {
    if (!inflight.current) {
      setMemberIds(new Set(initialMemberListIds));
    }
  }, [initialMemberListIds]);

  const toggleList = useCallback(
    async (listId: string, listName: string) => {
      if (isMobile) {
        setMobileDialogOpen(false);
      }

      if (inflight.current) return;

      const wasInList = memberIdsRef.current.has(listId);

      // Optimistic update
      setMemberIds((prev) => {
        const next = new Set(prev);
        if (wasInList) {
          next.delete(listId);
        } else {
          next.add(listId);
        }
        return next;
      });

      toastManager.add({
        title: wasInList ? `Removed from ${listName}` : `Added to ${listName}`,
        type: 'success',
      });

      inflight.current = true;
      try {
        if (wasInList) {
          await removeEventFromList({data: {eventId, listId}});
        } else {
          await addEventToList({data: {eventId, listId}});
        }
        router.invalidate();
      } catch (err) {
        // Revert optimistic update
        setMemberIds((prev) => {
          const reverted = new Set(prev);
          if (wasInList) {
            reverted.add(listId);
          } else {
            reverted.delete(listId);
          }
          return reverted;
        });
        toastManager.add({
          title:
            err instanceof Error ? err.message : `Failed to update ${listName}`,
          type: 'error',
        });
      } finally {
        inflight.current = false;
      }
    },
    [eventId, router, isMobile]
  );

  // Handle pending save after login redirect.
  // Wait until both profile and lists are available before consuming
  // the pending save from sessionStorage — the effect may fire before
  // the loader data arrives.
  useEffect(() => {
    if (!profile) return;
    if (lists.length === 0) return;

    const savedList = lists.find((l) => l.id === 'saved');
    if (!savedList) return;
    if (memberIdsRef.current.has('saved')) {
      consumePendingSave(eventId);
      return;
    }

    const hasPending = consumePendingSave(eventId);
    if (!hasPending) return;

    toggleList('saved', savedList.name);
  }, [profile, eventId, lists, toggleList]);

  const handleTriggerClick = useCallback(() => {
    if (!profile) {
      savePendingSave(eventId);
      navigate({
        to: '/login',
        search: {redirect: `/event/${eventId}`},
      });
    }
  }, [profile, eventId, navigate]);

  const handleNewListClick = useCallback(() => {
    if (isMobile) {
      setMobileDialogOpen(false);
    }
    setCreateModalOpen(true);
  }, [isMobile]);

  /** After a list is created via the modal, auto-add the event to it. */
  const handleListCreated = useCallback(
    (newList: {id: string; name: string}) => {
      toggleList(newList.id, newList.name);
    },
    [toggleList]
  );

  const isSavedToAnyList = memberIds.size > 0;

  const bookmarkIcon = (
    <Bookmark className={`w-4 h-4 ${isSavedToAnyList ? 'fill-current' : ''}`} />
  );

  // Unauthenticated: render button that redirects to login
  if (!profile) {
    return (
      <Button
        size={isMobile ? 'icon' : 'default'}
        aria-label="Save to list"
        onClick={handleTriggerClick}
        variant="overlay"
      >
        {bookmarkIcon}
        {!isMobile && <span className="text-sm">Save to List</span>}
      </Button>
    );
  }

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          className="bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 hover:text-white"
          aria-label={isSavedToAnyList ? 'Manage saved lists' : 'Save to list'}
          onClick={() => setMobileDialogOpen(true)}
        >
          {bookmarkIcon}
        </Button>
        <Modal
          open={mobileDialogOpen}
          onOpenChange={setMobileDialogOpen}
          title="Save to List"
        >
          <div className="flex flex-col gap-0.5">
            <ListItems
              lists={lists}
              memberIds={memberIds}
              onToggle={toggleList}
            />
            {lists.length > 0 && <div className="mx-2 my-1 h-px bg-border" />}
            <Button className="w-full" onClick={handleNewListClick}>
              <Plus className="size-4" />
              New List
            </Button>
          </div>
        </Modal>
        <CreateListModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onCreated={handleListCreated}
        />
      </>
    );
  }

  return (
    <>
      <Menu>
        <MenuTrigger
          className={buttonVariants({
            variant: 'outline',
            className:
              'bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 hover:text-white',
          })}
        >
          {bookmarkIcon}
          <span>Save to List</span>
        </MenuTrigger>
        <MenuPopup align="end" sideOffset={4}>
          <div className="min-w-48">
            <MenuListItems
              lists={lists}
              memberIds={memberIds}
              onToggle={toggleList}
            />
            {lists.length > 0 && <MenuSeparator />}
            <MenuItem onClick={handleNewListClick}>
              <Plus className="size-4" />
              New List
            </MenuItem>
          </div>
        </MenuPopup>
      </Menu>
      <CreateListModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={handleListCreated}
      />
    </>
  );
};
